import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import ParticlesBackground from "../ParticlesBackground";

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1516331138075-f3adc1e149cd?auto=format&fit=crop&w=2000&q=80')",
        }}
      />

      {/* Dark Overlay with Vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030014]/40 via-[#030014]/70 to-[#030014]" />
      <div className="absolute inset-0 bg-radial-gradient" style={{
        background: 'radial-gradient(ellipse at center, transparent 0%, rgba(3, 0, 20, 0.6) 70%)'
      }} />

      {/* Particles */}
      <ParticlesBackground variant="hero" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="flex justify-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-950/50 backdrop-blur-md border border-indigo-500/20 text-cyan-400 text-sm font-medium tracking-wide">
              <Sparkles className="w-4 h-4" />
              24 Modules en E-Learning
            </span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            variants={itemVariants}
            className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-none"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="block text-white">Quantic</span>
            <span className="block text-gradient-cosmic mt-2">Révolution</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="max-w-3xl mx-auto text-xl sm:text-2xl text-slate-300 font-light leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Révolutionnez votre pratique : Devenez un{" "}
            <span className="text-cyan-400">Maître en Thérapie Quantique</span>{" "}
            Multidimensionnelle
          </motion.p>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="max-w-2xl mx-auto text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            Libérez votre plein potentiel et apportez des transformations
            holistiques et durables à vos patients avec des méthodes à la pointe
            de l'innovation.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <a
              href="https://gillesdelieuze.com/checkout-seminaire-abondance-2/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="btn-primary group"
            >
              Je rejoins la Révolution Quantique
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
            <button
              data-testid="hero-secondary-button"
              className="btn-secondary flex items-center gap-2"
              onClick={() => {
                document.getElementById("program")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <Play className="w-4 h-4" />
              Découvrir le programme
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-3 gap-8 pt-12 max-w-2xl mx-auto"
          >
            {[
              { value: "24", label: "Modules" },
              { value: "48H+", label: "de Contenu" },
              { value: "1000+", label: "Thérapeutes Formés" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-light text-gradient-cosmic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500 mt-1 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-2 bg-cyan-400 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
